import React, { useState } from "react";
import {
  Alert,
  Box,
  Checkbox,
  Chip,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { format } from "date-fns";

const toDate = (value) => {
  if (!value) return null;
  if (value instanceof Date) return value;
  if (typeof value?.toDate === "function") return value.toDate();
  if (typeof value?._seconds === "number") return new Date(value._seconds * 1000);
  if (typeof value?.seconds === "number") return new Date(value.seconds * 1000);
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const RemoveButton = ({ disabled, onRemove, label = "Remove" }) => {
  const [loading, setLoading] = useState(false);
  return (
    <LoadingButton
      size="small"
      // Tighter than the default small button so it doesn't drive up row height.
      sx={{ minWidth: 0, px: 1, py: 0.25 }}
      loading={loading}
      disabled={disabled || loading}
      onClick={async (event) => {
        event.stopPropagation();
        try {
          setLoading(true);
          await onRemove?.();
        } finally {
          setLoading(false);
        }
      }}
    >
      {label}
    </LoadingButton>
  );
};

/**
 * Shared sticky-header table for all document-list surfaces:
 *   - DocumentManagementPanel (Embedded Documents tab)
 *   - ClinicalReferencesPanel  (Clinical References tab + QueryManagementModal fixed-list mode)
 *   - DocumentLibraryDialog    (library picker)
 *
 * Columns rendered conditionally:
 *   - leading checkbox (if `selection` prop provided; component owns select-all)
 *   - File (always) — name + caller-supplied chips, optional secondary detail line
 *   - End adornment (if any doc returns one) — right-aligned caption, e.g. "5 chunks"
 *   - Uploaded (always)
 *   - Action (if `onRemove` provided) — Remove button
 *
 * `isRowDisabled` greys the row, locks its checkbox, and hides its Remove button.
 * Pair it with `getRowDisabledChip` to label why (e.g. "Already attached").
 */
const DocumentTable = ({
  documents = [],
  loading = false,
  error = null,
  emptyMessage = "No documents.",
  disabled = false,
  selection,
  isRowDisabled = () => false,
  getRowDisabledChip = () => null,
  getExtraChips = () => [],
  getSecondaryDetails = () => null,
  getEndAdornment = () => null,
  onRemove,
  removeLabel = "Remove",
  rowClickToggles = false,
  maxHeight = 480,
}) => {
  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }
  if (error) {
    return (
      <Alert severity="error">
        {typeof error === "string" ? error : error.message || String(error)}
      </Alert>
    );
  }
  if (!documents.length) {
    return (
      <Paper variant="outlined" sx={{ p: 3, textAlign: "center" }}>
        <Typography variant="body2" color="text.secondary">
          {emptyMessage}
        </Typography>
      </Paper>
    );
  }

  const showCheckboxColumn = Boolean(selection);
  const showEndColumn = documents.some((doc) => getEndAdornment(doc));
  const showActionColumn = Boolean(onRemove);

  const selectableIds = showCheckboxColumn
    ? documents.filter((doc) => !isRowDisabled(doc)).map((doc) => doc.id)
    : [];
  const allSelected =
    selectableIds.length > 0 &&
    selectableIds.every((id) => selection.selectedIds.has(id));
  const someSelected =
    !allSelected &&
    selectableIds.some((id) => selection.selectedIds.has(id));

  const handleToggleAll = () => {
    if (!selection) return;
    if (allSelected) {
      selectableIds.forEach((id) => {
        if (selection.selectedIds.has(id)) selection.onToggle(id);
      });
      return;
    }
    selectableIds.forEach((id) => {
      if (!selection.selectedIds.has(id)) selection.onToggle(id);
    });
  };

  return (
    <TableContainer component={Paper} variant="outlined" sx={{ maxHeight }}>
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow>
            {showCheckboxColumn ? (
              <TableCell padding="checkbox">
                <Checkbox
                  size="small"
                  indeterminate={someSelected}
                  checked={allSelected}
                  onChange={handleToggleAll}
                  disabled={disabled || !selectableIds.length}
                  inputProps={{ "aria-label": "Select all" }}
                />
              </TableCell>
            ) : null}
            <TableCell sx={{ fontWeight: 700 }}>File</TableCell>
            {showEndColumn ? (
              <TableCell sx={{ fontWeight: 700 }} align="right" />
            ) : null}
            <TableCell sx={{ fontWeight: 700 }}>Uploaded</TableCell>
            {showActionColumn ? (
              <TableCell sx={{ fontWeight: 700 }} align="right" />
            ) : null}
          </TableRow>
        </TableHead>
        <TableBody>
          {documents.map((doc) => {
            const rowDisabled = isRowDisabled(doc);
            const isSelected =
              showCheckboxColumn && selection.selectedIds.has(doc.id);
            const createdAt = toDate(doc.createdAt);
            const uploadedLabel = createdAt ? format(createdAt, "MM/dd/yyyy") : "—";
            const uploadedTooltip = createdAt
              ? format(createdAt, "MMM d, yyyy h:mm a")
              : "";
            const extraChips = getExtraChips(doc) || [];
            const disabledChip = getRowDisabledChip(doc);
            const secondaryText = getSecondaryDetails(doc);
            const endAdornment = getEndAdornment(doc);
            const canClick =
              rowClickToggles && showCheckboxColumn && !rowDisabled && !disabled;

            return (
              <TableRow
                key={doc.id}
                hover={canClick}
                selected={isSelected}
                onClick={() => {
                  if (!canClick) return;
                  selection.onToggle(doc.id);
                }}
                sx={{
                  cursor: canClick ? "pointer" : "default",
                  opacity: rowDisabled ? 0.55 : 1,
                }}
              >
                {showCheckboxColumn ? (
                  <TableCell padding="checkbox">
                    <Checkbox
                      size="small"
                      checked={Boolean(isSelected)}
                      disabled={rowDisabled || disabled}
                      onChange={(event) => {
                        event.stopPropagation();
                        if (!rowDisabled) selection.onToggle(doc.id);
                      }}
                      onClick={(event) => event.stopPropagation()}
                      inputProps={{
                        "aria-label": `Select ${doc.fileName || "document"}`,
                      }}
                    />
                  </TableCell>
                ) : null}
                <TableCell>
                  <Box
                    sx={{
                      display: "flex",
                      flexWrap: "wrap",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{ wordBreak: "break-word", fontWeight: 500 }}
                    >
                      {doc.fileName || "Untitled"}
                    </Typography>
                    {extraChips.map((chipProps, index) => (
                      <Chip
                        key={`chip-${index}`}
                        size="small"
                        variant="outlined"
                        {...chipProps}
                      />
                    ))}
                    {disabledChip ? (
                      <Chip size="small" {...disabledChip} />
                    ) : null}
                  </Box>
                  {secondaryText ? (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: "block", mt: 0.25 }}
                    >
                      {secondaryText}
                    </Typography>
                  ) : null}
                </TableCell>
                {showEndColumn ? (
                  <TableCell align="right">
                    {endAdornment ? (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ whiteSpace: "nowrap" }}
                      >
                        {endAdornment}
                      </Typography>
                    ) : null}
                  </TableCell>
                ) : null}
                <TableCell>
                  <Tooltip title={uploadedTooltip}>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ whiteSpace: "nowrap" }}
                    >
                      {uploadedLabel}
                    </Typography>
                  </Tooltip>
                </TableCell>
                {showActionColumn ? (
                  <TableCell align="right" onClick={(event) => event.stopPropagation()}>
                    {!rowDisabled ? (
                      <RemoveButton
                        disabled={disabled}
                        onRemove={() => onRemove(doc)}
                        label={removeLabel}
                      />
                    ) : null}
                  </TableCell>
                ) : null}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default DocumentTable;
